import http from '~/shared/http';
import HorizontalItemList from './HorizontalItemList';
import Podcast from './Podcast/Podcast';
import CustomAd from './CustomAd/CustomAd';
import { useEffect, useState } from 'preact/hooks';
import type { DeliveryCard } from '~/utils/adTracking';

const slot = 'popular.horizontal';

export const DEFAULT_PODCAST_ARTWORK = '/images/podd_placeholder.webp';

interface PopularPodcastsResponse {
  data: PopularApiRecord[];
}

interface Popular {
  url: string;
  title: string;
  genre: string;
  images: Images;
}

interface Images {
  large?: string;
  small?: string;
  medium: string;
  mini?: string;
  srcset?: string;
}

interface PopularApiRecord extends Omit<Popular, 'images'> {
  images?: Partial<Images> | null;
}

interface AdResponse {
  data: AdApiRecord | null;
}

interface Ad extends DeliveryCard {
  title: string;
  categoryName: string;
  meta: Meta;
  images: Images;
  link: string;
  id: number;
}

interface AdApiRecord extends Omit<Ad, 'images'> {
  images?: Partial<Images> | null;
}

interface Meta {
  title: string;
}

function hasMediumImage<T extends { images?: Partial<Images> | null }>(
  record: T,
): record is T & { images: Images } {
  return typeof record.images?.medium === 'string' && record.images.medium.length > 0;
}

export function addDefaultPodcastArtwork(podcasts: PopularApiRecord[]): Popular[] {
  return podcasts.map((podcast) => ({
    ...podcast,
    images: {
      ...podcast.images,
      medium: podcast.images?.medium?.trim() || DEFAULT_PODCAST_ARTWORK,
    },
  }));
}

const actions = {
  async fetchPopular() {
    return await http.get<PopularPodcastsResponse>('/api/podcasts/popular');
  },
  async fetchAd(excludeIds?: string) {
    const params = new URLSearchParams({
      ...(excludeIds ? { exclude: excludeIds } : {}),
      s: slot,
    });
    return await http.get<AdResponse>(`/api/ad?${params.toString()}`);
  },
};

async function created(excludeIds?: string) {
  const result: {
    error: null;
    data: {
      popular: Popular[] | null;
      ad: Ad | null;
    };
  } = {
    error: null,
    data: {
      popular: null,
      ad: null,
    },
  };

  const [popularPodcasts, ad] = await Promise.allSettled([
    actions.fetchPopular(),
    actions.fetchAd(excludeIds),
  ]);

  if (popularPodcasts.status === 'fulfilled') {
    result.data.popular = addDefaultPodcastArtwork(popularPodcasts.value.data.data).filter((x) => {
      if (!result.data.ad) {
        return true;
      }
      return result.data.ad.link !== x.url;
    });
  }

  if (
    ad.status === 'fulfilled' &&
    ad.value.data.data !== null &&
    hasMediumImage(ad.value.data.data)
  ) {
    result.data.ad = { ...ad.value.data.data };
  }

  return result;
}
interface Props {
  title: string;
  itemCount?: number;
  action?: string;
}
export default function PopularPodcasts({ title, action, itemCount }: Props) {
  const [podcasts, setPodcasts] = useState<Popular[] | null>();
  const [ad, setAd] = useState<Ad | null>(null);
  const [loading, setLoading] = useState(true);
  const podcast = window.__INITIAL_STATE?.podcast;

  useEffect(() => {
    created(podcast ? podcast.itunes_id : undefined)
      .then((result) => {
        setPodcasts(result.data.popular);
        setAd(result.data.ad);
        setLoading(() => false);
      })
      .catch(() => {
        setLoading(() => false);
      });
  }, []);

  function refetchAd() {
    actions
      .fetchAd(podcast ? podcast.itunes_id : undefined)
      .then((result) => {
        const adData = result.data.data;
        setAd(adData && hasMediumImage(adData) ? { ...adData } : null);
      })
      .catch((error) => {
        console.error('Failed to fetch ad:', error);
      });
  }

  return (
    <section class="items-grid t-most-recent">
      <div class="my-4">
        {action ? (
          <a href={action} class="my0">
            <h2 class="medium-title my0">{title}</h2>
          </a>
        ) : (
          <h2 class="medium-title my0">{title}</h2>
        )}
      </div>

      <div class="row">
        <HorizontalItemList horizontalScroll={true}>
          {loading
            ? Array.from(Array(itemCount)).map(() => (
                <div className="media-block">
                  <div class="media-box ratio-container ratio1x1" />
                </div>
              ))
            : null}
          {podcasts?.slice(0, itemCount).map((podcast, index) => (
            <>
              <Podcast
                key={podcast.url}
                url={podcast.url}
                title={podcast.title}
                imageSrc={podcast.images.medium}
                genre={podcast.genre}
              />
              {ad && index === 0 ? (
                <CustomAd
                  key={ad.delivery_token ?? ad.id}
                  delivery={ad}
                  url={ad.link}
                  pid={ad.id}
                  src={ad.images.medium}
                  title={ad.title}
                  slogan={ad.meta.title}
                  refetchAd={refetchAd}
                  slotid={slot}
                />
              ) : null}
            </>
          ))}
        </HorizontalItemList>
      </div>
    </section>
  );
}
